import { useId, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cx } from './cx.js';
import { Input, type InputProps } from './Input.js';

/** What a control inside a Field needs for its label, hint and error to be announced. */
export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
}

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  /** Shown under the control, announced, and marks the control invalid. */
  error?: ReactNode;
  className?: string;
  children: (control: FieldControlProps) => ReactNode;
}

export function Field({ label, hint, error, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  return (
    <div className={cx('qk-field', Boolean(error) && 'qk-field--invalid', className)}>
      <label className="qk-field__label" htmlFor={id}>
        {label}
      </label>
      {children({
        id,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(error ? { 'aria-invalid': true as const } : {}),
      })}
      {hint ? (
        <p id={hintId} className="qk-field__hint">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="qk-field__error">
          <span aria-hidden="true" className="qk-field__error-icon">
            !
          </span>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends Omit<InputProps, 'id'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

/** A labelled input with hint and error: the common case of Field + Input. */
export function TextField({ label, hint, error, ...input }: TextFieldProps) {
  return (
    <Field label={label} hint={hint} error={error}>
      {(control) => <Input {...input} {...control} />}
    </Field>
  );
}

export interface TextAreaFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id'
> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

/** A labelled multi-line input. */
export function TextAreaField({ label, hint, error, className, ...rest }: TextAreaFieldProps) {
  return (
    <Field label={label} hint={hint} error={error}>
      {(control) => (
        <textarea {...rest} {...control} className={cx('qk-input', 'qk-textarea', className)} />
      )}
    </Field>
  );
}
